import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import MaskedTextField from "@/common/components/MaskedTextField";
import SelectController from "@/common/components/SelectController";
import TextField from "@/common/components/TextField";
import { nameMask, phoneMask } from "@/common/constants/masks.constant";
import { useCreateEstablishmentUser } from "@/features/establishments/hooks/useCreateEstablishmentUser";
import { establishmentUserSchema, EstablishmentUserValidationSchema } from "@/features/establishments/schemas/create.schema";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import { statusOptions } from "@/features/users/constants/statusOptions.constant";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useEstablishmentRoles } from "@/common/hooks/queries/useEstablishmentRoles";
import { Mapper } from "@/common/services/mapper";
import { RoleData } from "@/common/types/roles";
import useDebounce from "@/common/hooks/useDebounce";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import GoBackButton from "@/common/components/GoBackButton";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ImageUploader } from "@/common/components/ImageUploader";
import { checkIsImage } from "@/common/utils/fileValidator";

const Create = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<Blob>();

  const [userId, setUserId] = useState('');
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const { data: establishment } = useEstablishmentCompany(id || '');

  const [searchRole, setSearchRole] = useDebounce('');

  const { data: rolesOptions } = useEstablishmentRoles({
    filters: { search: searchRole },
    mapper: Mapper.mapToOptions<RoleData>({ labelFieldName: 'display_name', valueFieldName: 'id' })
  });

  const {
    mutate: createEstablishmentUser,
    isPending: createEstablishmentUserIsPending,
  } = useCreateEstablishmentUser({
    companyId: id,
    companyBranchId: establishment?.matrix_branch_id,
    file,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['establishmentUsers'] });

      const data = response.data.data;

      if (data.id) {
        setUserId(data.id);
      }

      setIsError(false);
      setMessages(['Senha enviada ao usuário por e-mail e/ou SMS.']);
    },
    onError: (error) => {
      setIsError(true);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);

      FormTransformers.errorsTransformer({ setError })(error);
    },
  });

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors }
  } = useForm<EstablishmentUserValidationSchema>({
    resolver: zodResolver(establishmentUserSchema)
  });

  const onSubmit: SubmitHandler<EstablishmentUserValidationSchema> = (data) => createEstablishmentUser(data);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4 items-center">
            <GoBackButton fallback={`/establishments/${id}/users`} />

            <h2 className="text-2xl font-bold">Novo usuário</h2>
          </div>

          <Button
            type="submit"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            isLoading={createEstablishmentUserIsPending}
          >
            Confirmar
          </Button>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">Dados pessoais</p>
          <ImageUploader
            isLoading={false}
            label="Foto"
            name="profile-picture"
            accept="image/*"
            onChange={(e) => {
              const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
              if (!file || !checkIsImage(file)) {
                return
              }
              setFile((e.target as HTMLInputElement & { files: FileList }).files[0])
            }}
            currentImageUrl={file
              ? URL.createObjectURL(file)
              : undefined
            }
            disabled={createEstablishmentUserIsPending}
            errorMessage={errors.profile_picture?.message as string | undefined}
          />
          <div className="flex gap-2 w-full justify-between">
            <div className="w-3/4">
              <MaskedTextField
                label="CPF"
                mask='000.000.000-00'
                errorMessage={errors.cpf?.message || undefined}
                {...register('cpf')}
                markAsRequired
                disabled={createEstablishmentUserIsPending}
              />
              <Button variant='link'>Importar dados</Button>
            </div>
            <MaskedTextField
              label="Data de nascimento"
              markAsRequired
              mask="00/00/0000"
              className="w-1/4"
              errorMessage={errors.date_of_birth?.message || undefined}
              {...register('date_of_birth')}
              disabled={createEstablishmentUserIsPending}
            />
          </div>
          <div className="flex gap-2 w-full justify-between">
            <MaskedTextField
              label="Nome"
              className="w-3/4"
              mask={nameMask}
              maxLength={64}
              errorMessage={errors.full_name?.message || undefined}
              {...register('full_name')}
              markAsRequired
              disabled={createEstablishmentUserIsPending}
            />
            <SelectController
              control={control}
              name="roles"
              label="Perfil"
              markAsRequired
              className="w-1/4"
              options={rolesOptions}
              onInputChange={setSearchRole}
              errorMessage={errors.roles?.message || undefined}
              isMulti
            />
          </div>
          <SelectController
            control={control}
            name="status"
            label="Status"
            options={statusOptions}
            errorMessage={errors.status?.message || undefined}
            markAsRequired
            isDisabled={createEstablishmentUserIsPending}
          />
          <MaskedTextField
            label="Telefone"
            mask={phoneMask}
            errorMessage={errors.phone?.number?.message || undefined}
            {...register('phone.number')}
            disabled={createEstablishmentUserIsPending}
          />
          <TextField
            label="Email"
            {...register('email')}
            errorMessage={errors.email?.message || undefined}
            disabled={createEstablishmentUserIsPending}
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => userId && navigate(`/establishments/${id}/users/${userId}`)}
        />
      )}
    </>
  );
};

export default Create;
